import { useLayoutEffect } from 'react';

type Stage = 'bootstrap' | 'auth' | 'home' | 'summary' | 'gym' | 'images' | 'fonts' | 'layout';
const stages: Stage[] = ['bootstrap', 'auth', 'home', 'summary', 'gym', 'images', 'fonts', 'layout'];

/** Completed startup milestones, not a network-byte estimate. One attempt per document. */
function complete(stage: Stage, terminal = false) {
  const splash = document.getElementById('startup-splash');
  if (!splash || splash.dataset.failed) return;
  const done = new Set((splash.dataset.completed ?? '').split(',').filter(Boolean));
  done.add(stage);
  splash.dataset.completed = [...done].join(',');
  const count = stages.filter(item => done.has(item)).length;
  // Milestones are historical; only the atomic readiness commit earns 100.
  const value = terminal ? 100 : Math.floor(count * 99 / stages.length);
  const bar = splash.querySelector<HTMLElement>('.startup-bar')!;
  bar.setAttribute('aria-valuenow', String(value));
  bar.setAttribute('aria-valuetext', `Готово этапов: ${count} из ${stages.length}`);
  bar.querySelector<HTMLElement>('i')!.style.width = `${value}%`;
}

function fail(splash: HTMLElement) {
  splash.dataset.failed = 'true';
  splash.querySelector<HTMLElement>('.startup-bar')!.hidden = true;
  splash.querySelector('[role="status"]')!.textContent = 'Не удалось загрузить данные. Проверьте соединение и повторите.';
  splash.querySelector<HTMLButtonElement>('#startup-retry')!.hidden = false;
}

export function failStartup() {
  const splash = document.getElementById('startup-splash');
  if (!splash) return false;
  fail(splash);
  return true;
}

/** Home mounts under the HTML shell: each independent request reports its completion. */
export function useHomeStartup(loading: boolean, error: boolean, ready: {home: boolean; summary: boolean; gym: boolean}) {
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    const splash = document.getElementById('startup-splash');
    if (!root || !splash) return;
    if (error) fail(splash);
    else for (const stage of ['home', 'summary', 'gym'] as const) if (ready[stage]) complete(stage);
    root.dataset.homeStartup = error ? 'error' : loading ? 'pending' : 'ready';
    return () => { delete root.dataset.homeStartup; };
  }, [loading, error, ready.home, ready.summary, ready.gym]);
}

const frame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

/** One-shot, route-aware reveal. No minimum duration and no background overlay. */
export function useStartupReveal(authReady: boolean, home: boolean, route: string) {
  useLayoutEffect(() => {
    const splash = document.getElementById('startup-splash');
    const root = document.getElementById('root');
    if (!splash || !root) return;
    complete('bootstrap');
    if (!authReady) return;
    complete('auth');
    // Login/onboarding/deeplinks have no Home dependencies to fetch.
    if (!home) for (const stage of ['home', 'summary', 'gym'] as const) complete(stage);
    let cancelled = false;
    let committed = false;
    let revision = 0;
    const check = async () => {
      if (committed) return;
      const current = ++revision;
      const active = () => !cancelled && current === revision && !splash.dataset.failed;
      if (!active() || !root.childElementCount) return;
      if (home && root.dataset.homeStartup === 'error') { fail(splash); return; }
      if (home && root.dataset.homeStartup !== 'ready') return;
      const visibleImages = () => Array.from(root.querySelectorAll('img')).filter(image => {
        const rect = image.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.top < innerHeight && rect.bottom > 0;
      });
      const images = visibleImages();
      const decoded = new Map<HTMLImageElement, string>();
      try {
        await Promise.all([
          document.fonts.ready.then(() => { if (active()) complete('fonts'); }),
          Promise.all(images.map(async image => {
            await image.decode();
            decoded.set(image, image.currentSrc);
          })).then(() => { if (active()) complete('images'); }),
        ]);
      } catch { if (active()) fail(splash); return; }
      await frame();
      await frame();
      if (!active()) return;
      complete('layout');
      await frame();
      await frame();
      if (!active()) return;
      // Re-enumerate after layout: head CSS/media changes need not mutate root.
      // Every currently visible source must have decoded in this check.
      const pendingImage = visibleImages().some(image => !image.complete ||
        image.naturalWidth === 0 || !decoded.has(image) || decoded.get(image) !== image.currentSrc);
      // Read current fonts after the layout read; no async gap before commit.
      if (observer.takeRecords().length || pendingImage || document.fonts.status === 'loading') {
        void check();
        return;
      }
      committed = true;
      observer.disconnect();
      document.fonts.removeEventListener('loading', recheck);
      root.inert = false;
      document.dispatchEvent(new Event('app-startup-ready'));
      complete('layout', true);
      // Readiness is now committed; later DOM work is normal post-startup work.
      // Keep the completed track for a painted frame, not another readiness wait.
      await frame();
      await frame();
      splash.remove();
    };
    const recheck = () => { void check(); };
    const observer = new MutationObserver(recheck);
    observer.observe(root, { childList: true, subtree: true, attributes: true });
    document.fonts.addEventListener('loading', recheck);
    void check();
    return () => { cancelled = true; observer.disconnect(); document.fonts.removeEventListener('loading', recheck); };
  }, [authReady, home, route]);
}
